import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

interface GlobalButtonProps {
  children: React.ReactNode;
  className?: string;
  onClick?: (e?: any) => void;
  onBlur?: (e?: any) => void;
  to?: string;
  variant?:
    | 'transparent'
    | 'primary'
    | 'secondary'
    | 'blue'
    | 'stroke'
    | 'redStroke'
    | 'purpleStroke'
    | 'secondaryRed'
    | 'purple'
    | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  type?: 'button' | 'submit' | 'reset';
  state?: 'default' | 'hover' | 'inactive' | 'loading' | 'disabled';
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  [key: string]: any;
}

const GlobalButton = ({
  children,
  className = '',
  onClick = undefined,
  onBlur = undefined,
  to = '',
  variant = 'purple',
  size = 'md',
  type = undefined,
  state = 'default',
  leftSection = null,
  rightSection = null,
  ...rest
}: GlobalButtonProps) => {
  const buttonClass = clsx(
    'rounded-lg',
    'font-bold',
    'flex',
    'items-center',
    'justify-center',
    'focus:outline-none',
    'whitespace-nowrap',
    'transition',
    'w-full',
    'duration-300',
    'ease-in-out',
    'text-center',
    'tracking-tight',
    'flex-shrink-0',

    {
      // Variant styles
      'border-none bg-transparent':
        variant === 'transparent' && state === 'default',
      'btn-primary text-white-1': variant === 'primary' && state === 'default',
      'btn-secondary text-gray-2':
        variant === 'secondary' && state === 'default',
      'btn-blue text-white-1': variant === 'blue' && state === 'default',
      'btn-stroke text-text-primary-body':
        variant === 'stroke' && state === 'default',
      'btn-red-stroke': variant === 'redStroke' && state === 'default',
      'btn-purple-stroke text-primaryLight':
        variant === 'purpleStroke' && state === 'default',
      'btn-secondary-red text-white-1 border border-red-2':
        variant === 'secondaryRed' && state === 'default',
      'btn-purple2 text-white-1': variant === 'purple' && state === 'default',
      'btn-neutral text-white-1': variant === 'neutral' && state === 'default',
      // Size styles
      'py-2 px-4 text-sm h-[36px]': size === 'sm',
      'py-3 px-6 text-base h-[44px]': size === 'md',
      'py-4 px-8 h-[56px]': size === 'lg',
      // State styles
      'cursor-pointer': state === 'default' || state === 'hover',
      'cursor-not-allowed btn-primary text-gray-1 border-1 border-black-1':
        state === 'inactive',
    },
    className,
    rest.className
  );

  if (to) {
    return (
      <Link to={to}>
        <a
          href={to}
          className={buttonClass}
          onClick={state !== 'default' ? (e) => e.preventDefault() : undefined}
          onBlur={onBlur}
        >
          {leftSection && <div className="mr-2">{leftSection}</div>}
          {children}
          {rightSection && <div className="ml-2">{rightSection}</div>}
        </a>
      </Link>
    );
  } else {
    return (
      <>
        <button
          type={type}
          className={buttonClass}
          onClick={onClick}
          onBlur={onBlur}
          disabled={state === 'inactive'}
          {...rest}
        >
          {leftSection && <div className="mr-2">{leftSection}</div>}
          {children}
          {rightSection && <div className="ml-2">{rightSection}</div>}
        </button>
      </>
    );
  }
};

export default GlobalButton;
